/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import styled from "styled-components";
import * as React from "react";
import { devices, fontFamilies } from "components/shared/Styles";
import enhanceJobIcon2x from "images/home/landing/Jobs/jtbd-enhance-icon@2x.png?sizes[]=128&sizes[]=64&responsive";
import recordJobIcon2x from "images/home/landing/Jobs/jtbd-record-icon@2x.png?sizes[]=128&sizes[]=64&responsive";
import transcribeJobIcon1x from "images/home/landing/Jobs/jtbd-transcribe-icon@1x.png";
import transcribeJobIcon2x from "images/home/landing/Jobs/jtbd-transcribe-icon@2x.png";
import editAudioJobIcon1x from "images/home/landing/Jobs/jtbd-edit-icon@1x.png";
import editAudioJobIcon2x from "images/home/landing/Jobs/jtbd-edit-icon@2x.png";
import checkMicJobIcon1x from "images/home/landing/Jobs/jtbd-miccheck-icon@1x.png";
import checkMicJobIcon2x from "images/home/landing/Jobs/jtbd-miccheck-icon@2x.png";
import { Analytics, AnalyticsEvent } from "constants/Analytics";
import { AppPath } from "components/AppPath";
import { useHistoryPush } from "utilities/NavigationUtilities";

interface LargeJobProps {
  title: string;
  subtitle: string;
  buttonText: string;
  iconSrcSet: string;
  gradient: string;
  path: string;
  analyticsEvent: AnalyticsEvent;
}

interface SmallJobProps {
  title: string;
  subtitle: string;
  iconSrcSet: string;
  path: string;
  analyticsEvent: AnalyticsEvent;
}

const LargeJob: React.FC<LargeJobProps> = ({
  title,
  subtitle,
  buttonText,
  iconSrcSet,
  gradient,
  path,
  analyticsEvent,
}) => {
  const historyPush = useHistoryPush();

  const handleClick = (): void => {
    Analytics.instance.logEvent(analyticsEvent);
    historyPush(path);
  };

  return (
    <LargeJobTile onClick={handleClick} $bgGradient={gradient}>
      <LargeJobIcon srcSet={iconSrcSet} alt="" />
      <LargeJobTitle>{title}</LargeJobTitle>
      <LargeJobSubtitle>{subtitle}</LargeJobSubtitle>
      <JobButton>{buttonText}</JobButton>
    </LargeJobTile>
  );
};

const SmallJob: React.FC<SmallJobProps> = ({
  title,
  subtitle,
  iconSrcSet,
  path,
  analyticsEvent,
}) => {
  const historyPush = useHistoryPush();

  const handleClick = (): void => {
    Analytics.instance.logEvent(analyticsEvent);
    historyPush(path);
  };

  return (
    <SmallJobTile onClick={handleClick}>
      <SmallJobIcon srcSet={iconSrcSet} alt="" />
      <SmallJobTextColumn>
        <SmallJobTitle>{title}</SmallJobTitle>
        <SmallJobSubtitle>{subtitle}</SmallJobSubtitle>
      </SmallJobTextColumn>
    </SmallJobTile>
  );
};

export const JobsLineup: React.FC = () => {
  return (
    <JobsLineupContainer>
      <JobsFirstRowContainer>
        <LargeJob
          title={I18n.t("landing.jobs.enhance.title")}
          subtitle={I18n.t("landing.jobs.enhance.subtitle")}
          buttonText={I18n.t("landing.jobs.enhance.action")}
          iconSrcSet={enhanceJobIcon2x.srcSet}
          gradient="linear-gradient(158deg, #5266FF 5.68%, #B871FF 96.33%)"
          path={AppPath.ENHANCE_SPEECH}
          analyticsEvent={AnalyticsEvent.JTBD_ENHANCE_HOME}
        />
        <LargeJob
          title={I18n.t("landing.jobs.record.title")}
          subtitle={I18n.t("landing.jobs.record.subtitle")}
          buttonText={I18n.t("landing.jobs.record.action")}
          iconSrcSet={recordJobIcon2x.srcSet}
          gradient="linear-gradient(160deg, #F82144 6.54%, #FF9F50 96.6%)"
          path={AppPath.RECORD_PAGE}
          analyticsEvent={AnalyticsEvent.JTBD_RECORD_HOME}
        />
      </JobsFirstRowContainer>
      <JobsSecondRowContainer>
        <SmallJob
          title={I18n.t("landing.jobs.transcribe.title")}
          subtitle={I18n.t("landing.jobs.transcribe.subtitle")}
          iconSrcSet={`${transcribeJobIcon1x} 1x, ${transcribeJobIcon2x} 2x`}
          path={AppPath.TRANSCRIPTION_PAGE}
          analyticsEvent={AnalyticsEvent.JTBD_TRANSCRIBE_HOME}
        />
        <SmallJob
          title={I18n.t("landing.jobs.edit.title")}
          subtitle={I18n.t("landing.jobs.edit.subtitle")}
          iconSrcSet={`${editAudioJobIcon1x} 1x, ${editAudioJobIcon2x} 2x`}
          path={AppPath.EDIT_AUDIO_PAGE}
          analyticsEvent={AnalyticsEvent.JTBD_EDIT_HOME}
        />
        <SmallJob
          title={I18n.t("landing.jobs.mic_check.title")}
          subtitle={I18n.t("landing.jobs.mic_check.subtitle")}
          iconSrcSet={`${checkMicJobIcon1x} 1x, ${checkMicJobIcon2x} 2x`}
          path={AppPath.MICROPHONE_CHECK}
          analyticsEvent={AnalyticsEvent.JTBD_MICCHECK_HOME}
        />
      </JobsSecondRowContainer>
    </JobsLineupContainer>
  );
};

const JobsLineupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: -104px auto 0 -34px;
  width: min(1232px, calc(100% + 68px));
  box-sizing: border-box;
  position: relative;
  z-index: 2;
  background: white;
  box-shadow: var(--phonos-box-shadow);
  border-radius: 64px;
  padding: 32px;
  justify-content: space-between;

  ${devices.medium} {
    padding: 24px;
    border-radius: 48px;
    width: calc(100% + 68px);
    min-width: 624px;
  }

  ${devices.small} {
    width: calc(100% - 48px);
    min-width: 312px;
    padding: 16px;
    margin: -104px auto 0;
    border-radius: 48px;
    gap: 16px;
  }
`;

const JobsFirstRowContainer = styled.div`
  gap: 32px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  box-sizing: border-box;
  width: 100%;

  ${devices.medium} {
    gap: 24px;
  }

  ${devices.small} {
    flex-direction: column;
    gap: 16px;
    padding: 0;
    margin: 0;
  }
`;

const JobsSecondRowContainer = styled.div`
  gap: 20px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
  box-sizing: border-box;
  width: 100%;

  ${devices.medium} {
    gap: 24px;
  }

  ${devices.small} {
    flex-direction: column;
    gap: 16px;
    padding: 0;
    margin: 0;
  }
`;

const LargeJobTile = styled.div<{ $bgGradient: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  border-radius: 32px;
  background: ${(props): string => props.$bgGradient};
  cursor: pointer;
  min-height: 227px;
  flex: 1;
  min-width: 280px;
  box-sizing: border-box;
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;

  &:hover {
    button {
      box-shadow: none;
    }

    background: ${(props): string =>
      `linear-gradient(0deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.15) 100%), ${props.$bgGradient}`};
    transform: translateY(-4px);
    box-shadow: var(--phonos-box-shadow-heavy);
  }

  ${devices.medium} {
    min-height: 200px;
  }

  ${devices.small} {
    width: 100%;
    padding: 24px;
  }
`;

const SmallJobTile = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 16px;
  border-radius: 32px;
  background: #ebebff;
  cursor: pointer;
  min-height: 88px;
  flex: 1;
  min-width: 280px;
  box-sizing: border-box;
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;

  &:hover {
    background:
      linear-gradient(
        0deg,
        rgba(255, 255, 255, 0.2) 0%,
        rgba(255, 255, 255, 0.2) 100%
      ),
      #ebebff;
    transform: translateY(-2px);
    box-shadow: var(--phonos-box-shadow-heavy);
  }

  ${devices.medium} {
    min-width: 250px;
  }

  ${devices.small} {
    width: 100%;
    padding: 16px;
  }
`;

const LargeJobIcon = styled.img`
  width: 64px;
  height: 64px;
  margin-bottom: 12px;
  box-shadow: var(--phonos-box-shadow-heavy);
  border-radius: 16px;
`;

const SmallJobIcon = styled.img`
  width: 36px;
  height: 36px;
  margin: 0 10px;
`;

const LargeJobTitle = styled.h2`
  font: normal 900 22px/130% ${fontFamilies};
  color: white;
  margin: 0 0 2px 0;
  text-align: center;
`;

const LargeJobSubtitle = styled.p`
  font: normal 500 16px/100% ${fontFamilies};
  color: white;
  margin: 0 0 14px 0;
  text-align: center;
`;

const SmallJobTextColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  margin-left: 8px;
`;

const SmallJobTitle = styled.h2`
  font: normal 800 18px/130% ${fontFamilies};
  color: black;
  margin: 0 0 2px 0;
  text-align: left;
`;

const SmallJobSubtitle = styled.p`
  font: normal 500 16px/100% ${fontFamilies};
  color: black;
  text-align: left;
`;

const JobButton = styled.button`
  height: 40px;
  min-height: 40px;
  background: #ffffffe5;
  border: none;
  border-radius: 100px;
  padding: 0 20px;
  font: normal 800 16px/100% ${fontFamilies};
  color: var(--spectrum-global-color-gray-900);
  cursor: pointer;
  box-shadow: var(--phonos-box-shadow-heavy);
`;
